import { MouseEvent, useCallback, useEffect, useState } from "react";

import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";

import { fetchPlaceOptions } from "@/apis/place";
import { postReservationsByState } from "@/apis/reservation";
import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown/Dropdown";
import Pagination from "@/components/Pagination";
import type { SortDirection, SortState } from "@/components/Table";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import BatchGroupList from "@/pages/Admin/components/ReservationInfo/BatchGroupList";
import ReservationTable from "@/pages/Admin/components/ReservationInfo/ReservationTable";
import TableFilterButton from "@/pages/Admin/components/TableFilterButton";
import DatePickerButton from "@/pages/Reservation/components/DropdownDatePicker";
import { ReservationByState, ReservationStateCode } from "@/types/reservation";

const filterButtons = [
  { name: "승인 요청", type: "request" },
  { name: "승인 완료", type: "approve" },
  { name: "승인 반려", type: "reject" },
  { name: "예약 취소", type: "cancel" },
  { name: "삭제", type: "deleted" },
  { name: "일괄 그룹", type: "batch" },
];

const today = dayjs();

const ReservationInfo = () => {
  const { control, getValues, setValue } = useForm({
    defaultValues: {
      plcCd: {
        id: "all",
        name: "장소 선택",
      },
      fromDate: today,
      toDate: today.add(30, "day"),
    },
  });

  const [placeList, setPlaceList] = useState<any[]>([]);

  const [reservations, setReservations] = useState<ReservationByState[]>([]);
  const [selectedSttCd, setSelectedSttCd] = useState<
    ReservationStateCode | "all" | "batch"
  >("all");
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [requestCount, setRequestCount] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    column: "날짜",
    direction: "desc",
  });

  // 프론트 컬럼명 → 서버 sortColumn 매핑
  const columnToServerKey: Record<string, string> = {
    날짜: "date",
    장소: "place",
    예약자: "user",
  };

  // 예약 내역 리스트 가져오는 API
  const getReservations = async () => {
    const plcCd = getValues("plcCd").id;
    const fromDate = getValues("fromDate").format("YYYYMMDD");
    const toDate = getValues("toDate").format("YYYYMMDD");

    const serverCol = columnToServerKey[sortState.column] || "";
    const serverDir = sortState.direction || "";

    const { content, totalElements, totalPages } =
      await postReservationsByState(
        page,
        10,
        selectedSttCd,
        plcCd,
        fromDate,
        toDate,
        searchName,
        serverCol,
        serverDir,
      );
    setReservations(content);
    setPageCount(totalPages);
    selectedSttCd === "request" && setRequestCount(totalElements);
  };

  const handleSort = useCallback((column: string, direction: SortDirection) => {
    setSortState({ column, direction });
    setPage(0);
  }, []);

  useEffect(() => {
    getPlaceList();
  }, []);

  const getPlaceList = async () => {
    setPlaceList(await fetchPlaceOptions("all", { includeAll: true }));
  };

  const getRequestCount = async () => {
    const { totalElements } = await postReservationsByState(
      page,
      10,
      "request",
    );
    setRequestCount(totalElements);
  };

  const handleClickFilterButton = (e: MouseEvent<HTMLButtonElement>) => {
    const filterType = e.currentTarget.dataset.filter as
      | ReservationStateCode
      | "all"
      | "batch";

    setSelectedSttCd(filterType);
    setValue("plcCd", { id: "all", name: "전체" });
    setValue("fromDate", dayjs());
    setValue("toDate", dayjs().add(30, "day"));
    setSearchName("");
    setPage(0);
  };

  const updateReservations = () => {
    getReservations();
  };

  const debouncedGetReservations = useDebouncedCallback(getReservations, 300);

  useEffect(() => {
    getReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSttCd, page, sortState]);

  useEffect(() => {
    getRequestCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex w-full flex-col gap-4 p-4 text-base text-black">
      <div className="flex flex-row justify-between">
        <div className="relative flex gap-2">
          <TableFilterButton
            data-filter="all"
            isActive={selectedSttCd === "all"}
            onClick={handleClickFilterButton}
          >
            전체
          </TableFilterButton>
          <div className="mx-1 border-r border-gray-dull"></div>
          {filterButtons.map(({ name, type }) => (
            <TableFilterButton
              data-filter={type}
              className="px-2 text-small"
              isActive={selectedSttCd === type}
              isNew={type === "request" && requestCount > 0}
              onClick={handleClickFilterButton}
            >
              {name}
            </TableFilterButton>
          ))}
        </div>
      </div>

      {selectedSttCd === "batch" ? (
        <BatchGroupList />
      ) : (
        <>
          <div className="flex flex-1 flex-row flex-wrap items-center gap-2">
            <div className="w-40">
              <Controller
                control={control}
                name="plcCd"
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    category="plcCd"
                    options={placeList}
                    disabled={false || placeList.length === 0}
                    onChangeOption={onChange}
                    selectedOption={value.name}
                  />
                )}
              />
            </div>

            <div className="w-40">
              <Controller
                name="fromDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <DatePickerButton
                    date={value}
                    onChange={e => {
                      onChange(e);
                    }}
                  />
                )}
              />
            </div>
            <div className="w-40">
              <Controller
                name="toDate"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <DatePickerButton
                      date={value}
                      startDate={value}
                      onChange={e => {
                        onChange(e);
                      }}
                    />
                  );
                }}
              />
            </div>

            <div className="w-36">
              <input
                type="text"
                placeholder="이름 검색"
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    setPage(0);
                    debouncedGetReservations();
                  }
                }}
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary"
              />
            </div>
            <div className="">
              <Button
                variant="outlined"
                onClick={() => {
                  setPage(0);
                  debouncedGetReservations();
                }}
              >
                조회
              </Button>
            </div>
          </div>

          <div className="flex flex-col items-center gap-6 bg-white px-3 py-4">
            <ReservationTable
              reservations={reservations}
              updateReservations={updateReservations}
              sortState={sortState}
              onSort={handleSort}
            />
            {pageCount > 0 && (
              <Pagination count={pageCount} page={page} onChange={setPage} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReservationInfo;
